/**
 * AcceptInvite page - Handles team invitation acceptance flow
 * Supports both authenticated and unauthenticated users
 */

import { Loader2, CheckCircle, XCircle, AlertCircle, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuth } from '@/context/AuthContext';
import { useInvitePreview, useAcceptInvite } from '@/hooks/useTeams';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data: preview, isLoading: previewLoading, error: previewError } = useInvitePreview(token || '');
  const acceptInvite = useAcceptInvite();

  const [accepted, setAccepted] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    // Auto-accept when authenticated user loads a valid invite
    if (isAuthenticated && preview?.valid && !accepted && !acceptInvite.isPending) {
      handleAccept();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, preview?.valid]);

  const handleAccept = async () => {
    if (!token) return;

    try {
      const result = await acceptInvite.mutateAsync(token);

      if (result.success) {
        setAccepted(true);
        // Redirect to the team's projects after a short delay
        setTimeout(() => {
          navigate('/projects');
        }, 2000);
      } else if (result.requiresAuth) {
        // Store token and redirect to login
        sessionStorage.setItem('pendingInviteToken', token);
        navigate('/login', { state: { from: `/invite/${token}` } });
      } else if (result.alreadyMember) {
        setAcceptError('You are already a member of this team');
        setTimeout(() => navigate('/projects'), 2000);
      }
    } catch (err) {
      if (err instanceof Error) {
        setAcceptError(err.message);
      } else {
        setAcceptError('Failed to accept invitation');
      }
    }
  };

  const handleLoginRedirect = () => {
    if (token) {
      sessionStorage.setItem('pendingInviteToken', token);
    }
    navigate('/login', { state: { from: `/invite/${token}` } });
  };

  const handleRegisterRedirect = () => {
    if (token) {
      sessionStorage.setItem('pendingInviteToken', token);
    }
    navigate('/register', { state: { from: `/invite/${token}`, email: preview?.email } });
  };

  // Loading state
  if (authLoading || previewLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-12">
            <Loader2 className="h-12 w-12 text-brand animate-spin mx-auto mb-4" />
            <p className="text-secondary">Loading invitation...</p>
          </div>
        </Card>
      </div>
    );
  }

  // Error loading preview
  if (previewError || !preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-12">
            <XCircle className="h-16 w-16 text-danger mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-primary mb-2">
              Invalid Invitation
            </h1>
            <p className="text-secondary mb-6">
              This invitation link is invalid or may have been revoked.
            </p>
            <Link to="/login">
              <Button variant="secondary">Go to Login</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Expired invite
  if (preview.expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-12">
            <AlertCircle className="h-16 w-16 text-warning mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-primary mb-2">
              Invitation Expired
            </h1>
            <p className="text-secondary mb-2">
              This invitation for <span className="font-medium">{preview.email}</span> has expired.
            </p>
            <p className="text-sm text-muted mb-6">
              Please ask the team admin to send a new invitation.
            </p>
            <Link to="/login">
              <Button variant="secondary">Go to Login</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Already accepted
  if (preview.alreadyAccepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-12">
            <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-primary mb-2">
              Already Accepted
            </h1>
            <p className="text-secondary mb-6">
              This invitation has already been accepted.
            </p>
            <Link to="/projects">
              <Button>Go to Projects</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Successfully accepted
  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-12">
            <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-primary mb-2">
              Welcome to {preview.teamName}!
            </h1>
            <p className="text-secondary mb-6">
              You have successfully joined the team.
            </p>
            <p className="text-sm text-muted">
              Redirecting to your projects...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Error accepting
  if (acceptError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-12">
            <XCircle className="h-16 w-16 text-danger mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-primary mb-2">
              Could Not Accept Invitation
            </h1>
            <p className="text-secondary mb-6">{acceptError}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="secondary" onClick={() => setAcceptError(null)}>
                Try Again
              </Button>
              <Link to="/projects">
                <Button>Go to Projects</Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Valid invite - show acceptance UI
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-6">
            <Users className="h-8 w-8 text-brand" />
          </div>

          <h1 className="text-2xl font-bold text-primary mb-2">
            Join {preview.teamName}
          </h1>

          <p className="text-secondary mb-1">
            <span className="font-medium">{preview.inviterName}</span> has invited you to collaborate on AdVerdict
          </p>

          <p className="text-sm text-muted mb-8">
            Invitation for {preview.email}
          </p>

          {isAuthenticated ? (
            // Authenticated user - show accept button
            <Button
              onClick={handleAccept}
              loading={acceptInvite.isPending}
              className="w-full"
              size="lg"
            >
              Accept Invitation
            </Button>
          ) : (
            // Unauthenticated user - show login/register options
            <div className="space-y-4">
              <p className="text-sm text-secondary">
                Sign in or create an account to accept this invitation
              </p>

              <div className="flex flex-col gap-3">
                <Button onClick={handleLoginRedirect} className="w-full" size="lg">
                  Sign In
                </Button>
                <Button
                  onClick={handleRegisterRedirect}
                  variant="secondary"
                  className="w-full"
                  size="lg"
                >
                  Create Account
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-muted mt-6">
            This invitation expires on{' '}
            {new Date(preview.expiresAt!).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        </div>
      </Card>
    </div>
  );
}
